/**
 * Sync coordination constants
 * Centralized to ensure tests and services import same values
 */

import { Brand } from '@enfomentis/shared/config';

/**
 * BroadcastChannel name for cross-tab coordination
 */
export const SYNC_CHANNEL = `${Brand.cachePrefix}-sync`;

/**
 * localStorage key for BroadcastChannel fallback
 * Used when BroadcastChannel is unavailable (Safari < 15.4, older browsers)
 */
export const SYNC_FALLBACK_KEY = `${Brand.cachePrefix}-sync-broadcast`;

/**
 * localStorage key for sync export lock
 * Reused from existing SyncEngine implementation
 */
export const SYNC_LOCK_KEY = `${Brand.cachePrefix}_sync_lock`;

/**
 * Export lock TTL in milliseconds (30 seconds)
 * Prevents deadlocks from crashed tabs
 */
export const SYNC_LOCK_TTL_MS = 30000;

/**
 * Records excluded from sync exports by table name.
 *
 * These records exist in local IndexedDB but are intentionally omitted from
 * baseline and delta exports. This is a policy decision, not a technical limitation.
 *
 * RATIONALE for beta-application-draft:
 * - The beta application workflow is separate from onboarding
 * - Including it in sync pollutes cloud storage with unrelated records
 * - Beta application data should remain device-local only
 *
 * OWNERSHIP: Product decision - contact product team before modifying.
 */
export const SYNC_EXCLUDED_RECORDS: Readonly<Partial<Record<string, readonly string[]>>> = {
  wizard_drafts: [`${Brand.cachePrefix}-beta-application-draft`],
} as const;

/**
 * Current sync schema version.
 *
 * This version identifies the structure of sync export files (baseline and delta).
 * It must be bumped when making breaking changes that affect sync compatibility.
 *
 * **When to bump this version:**
 * - Adding/removing/renaming fields in synced table schemas
 * - Changing field types in synced tables
 * - Modifying the export payload structure (BaselineExport, DeltaExport)
 * - Any change that would cause older exports to be incompatible
 *
 * **Synced tables are defined in:** `SYNC_TABLES` (src/constants/syncTables.ts)
 *
 * **Version history:**
 * - v1: Initial schema version (Dec 2025)
 * - v2: Operation-based sync format (Dec 2025)
 * - v3: Added id field to PreTradingSettings.healthMetrics.metrics (Dec 2025)
 * - v4: Added id field to PreTradingSettings.personalPrepFields (Dec 2025)
 * - v5: Id-based schemas for session data (Dec 2025)
 *       - PreTradingSettings: dropdown options changed from string[] to DropdownOption[]
 *       - SessionDraft: healthMetrics/personalPrep changed to Record<id, value>
 *       - Session: healthMetricsSnapshot/personalPrepSnapshot changed to Record<id, {value, name, ...}>
 * - v6: Config snapshots in SessionDraft (Dec 2025)
 *       - PreTradingSettings: added sectionConfig for section visibility/ordering
 *       - SessionDraft: added preTradingConfig, liveTradingConfig, postTradingConfig snapshots
 * - v7: Settings Changed modal ack hash (Dec 2025)
 *       - SessionDraft: added preTradingConfigAckHash for suppressing mismatch modal
 * - v8: Objectives system types (Jan 2026)
 *       - Added trading_objective_settings table to SYNC_TABLES
 *       - PreTradingSettings: added QueuedObjective type and queuedObjectives field
 * - v9: ObjectiveType ID-based schema (Jan 2026)
 *       - TradingObjectiveSettings: objectiveTypes changed from string[] to ObjectiveType[]
 *       - TradingObjective.type now stores ObjectiveType.id (not name)
 * - v10: Image storage infrastructure (Jan 2026)
 *       - Added stored_images table to SYNC_TABLES (image metadata)
 *       - Note: stored_image_blobs is local-only, NOT synced
 * - v11: Market snapshot settings (Jan 2026)
 *       - Added market_snapshot_settings table to SYNC_TABLES
 *       - Removed snapshotFields from PreTradingSettings (moved to MarketSnapshotSettings)
 *       - Removed snapshotFields from PreTradingConfigSnapshot (SessionDraft)
 *       - Removed preMarketAnalysis from SessionDraft
 *       - MarketStateDraft.fieldConfigSnapshot: name→fieldId, added 'url' type
 *       - MarketState/MarketStateDraft: File|null → ImageReference|null
 * - v12: Pre-market snapshot workflow infrastructure (Jan 2026)
 *       - SessionDraft.marketStates changed from singular MarketStateDraft to MarketStateDraft[]
 *       - MarketSnapshotSettings added defaultSnapshots field for auto-populating snapshots
 * - v13: Timeframe options in BasicSettings (Jan 2026)
 *       - BasicSettings: added timeframeOptions field (TimeframeOption[])
 * - v14: Default snapshot instrumentId rename (Jan 2026)
 *       - MarketSnapshotSettings.defaultSnapshots: renamed `instrument` → `instrumentId`
 * - v15: Instrument snapshot for market state (Jan 2026)
 *       - MarketState: replaced `instrumentId` with required `instrumentSnapshot` for historical accuracy
 *       - MarketStateDraft: replaced `instrumentId` with optional `instrumentSnapshot` (mirrors TradeDraft pattern)
 * - v16: Market snapshot fields ack hash (Jan 2026)
 *       - SessionDraft: added marketSnapshotFieldsAckHash for suppressing mismatch modal
 * - v17: Economic calendar schema updates (Jan 2026)
 *       - EconomicRelease: renamed currency→country, added source/providerEventId/higherIsBullish,
 *         replaced observedImpact→observedImpacts array, removed dxyImpact
 *       - EconomicEventDraft: mirrored EconomicRelease changes
 *       - Added EconomicCalendarSettings table (apiEnabled, countryFilters, minimumImpactLevel)
 *       - Removed economicImpactAsset from PreTradingSettings and PreTradingConfigSnapshot
 * - v18: Impact level multi-select (Jan 2026)
 *       - EconomicCalendarSettings: replaced minimumImpactLevel with impactLevels array
 *       - Allows any combination of impact levels instead of threshold-based filtering
 * - v19: Note entity foundation (Jan 2026)
 *       - Added notes table to SYNC_TABLES (persisted notes with ownership model)
 *       - Added note_drafts table to SYNC_TABLES (work-in-progress notes)
 *       - NoteDraft: noteType 'session'|'trade'|'economic'|'objective', required ownerId
 *       - Note: adds 'analysis' noteType with optional ownerId (undefined for standalone notes)
 * - v20: Removed preSessionNotes from objective types (Jan 2026)
 *       - ObjectiveDraft: removed preSessionNotes field (now stored as NoteDraft entity)
 *       - TradingObjective.usageHistory: removed preSessionNotes field (now stored as Note entity)
 * - v21: Removed observedImpacts from economic events (Jan 2026)
 *       - EconomicRelease: removed observedImpacts field and ObservedImpactEntry interface
 *       - EconomicEventDraft: removed observedImpacts field and ObservedImpactEntryDraft interface
 *       - Observation data now stored in Note entity with noteType: 'economic'
 * - v22: Per-section ack hashes for SettingsChangedModal (Jan 2026)
 *       - SessionDraft: replaced preTradingConfigAckHash with per-section hashes:
 *         healthMetricsAckHash, personalPrepAckHash, sectionVisibilityAckHash
 *       - marketSnapshotFieldsAckHash unchanged (already per-section)
 * - v23: Final checks ID-based schema (Jan 2026)
 *       - PreTradingSettings.finalCheckItems: string[] → Array<{ id, label }>
 *       - PreTradingConfigSnapshot.finalCheckItems: same upgrade
 *       - SessionDraft.finalChecks: checkItem → checkItemId + label
 *       - Session.finalChecksSnapshot: same upgrade
 * - v24: Final checks ack hash for divergence detection (Jan 2026)
 *       - SessionDraft: added finalCheckItemsAckHash for suppressing mismatch modal
 * - v25: Transition quotes ID-based schema (Jan 2026)
 *       - PreTradingSettings.transitionQuotes: string[] → TransitionQuote[]
 *       - TransitionQuote: { id, text, author? } for stable D&D and author attribution
 * - v26: Confluence neutral polarity model (Jan 2026)
 *       - Renamed: NegativeConfluence → Confluence (inherently neutral)
 *       - Renamed: negative_confluences table → confluences
 *       - Renamed: NegativeConfluenceSnapshot → ConfluenceSnapshot (+ polarity field)
 *       - Playbook.negativeConfluenceIds → Playbook.confluences (PlaybookConfluenceLink[])
 *       - Trade/TradeOpportunity/TradeDraft: negativeConfluenceSnapshot → confluenceSnapshot
 * - v27: Live Trading schema foundations (Feb 2026)
 *       - SessionDraft: added breakEvents (BreakEvent[]) for behavioral break logging
 *       - SessionDraft: LiveTradingConfigSnapshot upgraded from placeholder to interface
 *         with snapshotVersion, capturedAt, showClock, clockStyle base fields
 *       - New type: BreakEvent with BreakRoutineItemSnapshot
 * - v28: Phase timing fields (Feb 2026)
 *       - SessionDraft: added phaseElapsedMs (per-phase accumulated milliseconds)
 *       - SessionDraft: added currentPhaseStartedAt (ISO timestamp of current phase stint)
 *       - New type: TimedPhaseStatus (excludes 'shelved' from timed phases)
 *       - Note: Sync schema version bump only — no Dexie table schema migration required
 * - v29: Layout configuration persistence (Feb 2026)
 *       - LiveTradingSettings: added layoutConfig (LayoutConfig) for pane layout presets,
 *         working layout, banner config, and proportional pane weights
 *       - New service: LiveTradingSettingsService
 * - v30: Live trade capture foundations (Feb 2026)
 *       - Note: added NoteTradePhase type ('entry'|'management'|'exit'|'review') and tradePhase? field
 *       - NoteDraft: added tradePhase? field (mirrors Note.ts)
 *       - Trade: images field updated from raw inline objects to TradeImageRef[]
 *         TradeImageRef supports both imageId (ImageStorageService) and url (external)
 *       - TradeDraft: phase1.images updated from raw inline objects to TradeImageRef[]
 *       - LiveTradingSettings: replaced quickTradePlaybooks: string[] with
 *         quickTradePresets: QuickTradePreset[] (new interface with id, name, order + optional refs)
 *       - LiveTradingConfigSnapshot: added WU02 snapshot fields (noteCategories, showQuickTrades,
 *         activeTradesView, activeTradesDefaultSort, activeTradesCardInfo)
 * - v31: Trade schema alignment + TradeDraft split (Feb 2026) [P05 S02 WU2A T01]
 *       - TradeDraft: split into discriminated union TradeSetupDraft | OpportunityDraft
 *         - TradeDraftBase: added sessionDraftId, originSessionDraftId, carriedFromDraftId?,
 *           artifactKind, workflowStatus, isShelved, carryForwardDecision, tradeName (moved from phase1)
 *         - TradeSetupDraft: artifactKind='trade', phase1 without tradeName
 *         - OpportunityDraft: artifactKind='opportunity', opportunityKind, phase2Opportunity?
 *         - Removed: phase2Trade, phase1Execution, calculated, phase1.tradeName, type, status (DraftStatus)
 *       - Trade: added workflowStatus, isShelved, setupSessionId?;
 *         removed lessonsLearned, wouldRepeat, reviewNotes (post-trading sprint scope)
 *       - TradeOpportunity: added opportunityKind: 'missed' | 'observation' (required);
 *         made instrumentSnapshot, reasonCategorySnapshot, reasonSnapshot optional
 * - v32: Cockpit interaction polish — BannerConfig (Feb 2026) [P05 S02 WU01 T13]
 *       - BannerConfig: added widgetOrder? (BannerWidgetId[]) for explicit widget render order,
 *         widgetAlignment? (BannerAlignment) for single-widget alignment
 * - v33: Clock alarm configuration (Feb 2026) [P05 S02 WU01 T14]
 *       - LiveTradingSettings: added alarmRules? (AlarmRule[]) for user-configured time-based
 *         toast alerts during live trading (4 rule types: candle-close-countdown, fixed-interval,
 *         clock-mark, specific-time)
 *       - LiveTradingSettings: added alarmAutoDismissMs? (number) for global alarm toast
 *         auto-dismiss duration (default: 15000ms)
 * - v34: Saved layouts architecture (Feb 2026) [P05 S02 WU01 T12]
 *       - LayoutConfig: added savedLayouts? (SavedLayout[]) for named layout snapshots,
 *         activeLayoutId? (string) for tracking which saved layout is currently applied
 *       - DEFAULT_LAYOUT_SCHEMA_VERSION bumped from 1 to 2
 * - v35: Autosave toggle (Feb 2026) [P05 S02 WU01 T12C]
 *       - LayoutConfig: added autosaveEnabled? (boolean) for autosave toggle control
 *       - DEFAULT_LAYOUT_SCHEMA_VERSION bumped from 2 to 3
 * - v36: Trade post-review fields restored (Feb 2026) [P05 S02 WU2B T01 precursor]
 *       - Trade: restored lessonsLearned? and wouldRepeat? ('Yes'|'Maybe'|'No')
 *         (removed in v31 as post-trading sprint scope; restored for post-promotion review)
 * - v37: Execution foundation — promotion boundary + event-projection model (Feb 2026) [P05 S02 WU2B T01]
 *       - TradeDraftBase: added rootDraftId (stable lineage key for promotion idempotency)
 *       - TradeSetupDraft: removed phase2Execution? and qualitativeReview?
 *         (execution data belongs on persistent entities post-promotion, not draft layer)
 *       - TradeSetupDraft: added accountId? (pre-promotion account selection)
 *       - TradeExecution: rewritten to event-projection model; removed flat execution fields
 *         (direction, entryPrice, exitPrice, transactionCosts, etc.); added executionStatus,
 *         reconciliationStatus, reconciliationSource, projection fields (remainingQuantity,
 *         avgEntryPrice, avgExitPrice, stopLossCurrent, takeProfitCurrent, openedAt, closedAt),
 *         sourceDraftId, openedSessionId, closedSessionId?
 *       - TradeExecutionEvent: new type — immutable append-only event log with operationId,
 *         eventType, occurredAt, sparse payload fields (direction, quantity, price, etc.)
 *       - Trade: added sourceDraftId, rootDraftId (draft lineage linkage);
 *         made playbookSnapshot?, criteriaSnapshot?, confluenceSnapshot?, entryModelSnapshot?
 *         optional (required for promotion from partial drafts)
 *       - SessionDraft: trades changed from required TradeDraft[] to optional trades?
 *         (canonical ownership via TradeDraft.sessionDraftId, not session container)
 *       - BalanceHistory: added sourceExecutionEventId? (canonical execution event linkage)
 * - v38: Carry-forward lineage retirement (Mar 2026) [P05 S02 WU2C T02]
 *       - Added retired_draft_lineages table to SYNC_TABLES
 *         (durable "Delete Forever" retirement records keyed by rootDraftId)
 * - v39: Trading setup intent declaration (Mar 2026) [P05 S01 WU7C T02]
 *       - SessionDraft: added trading setup fields (activePlaybookSelections,
 *         defaultAccountId, focusInstrumentIds, riskSnapshot, setupConfirmed,
 *         tradingSetupAckHash)
 * - v40: Session intent contract (Mar 2026) [P05 S01 WU7C T03]
 *       - Session: added tradingSetupSnapshot (TradingSetupSnapshot) for
 *         finalized intent data persisted at session promotion
 * - v41: Trading setup settings table (Mar 2026) [P05 S01 WU7C T05]
 *       - trading_setup_settings: NEW table for default strategy-playbook
 *         pairs, account, and focus instrument preferences
 * - v42: Rename riskConfirmed → setupConfirmed (Mar 2026) [P05 S01 WU7C T07]
 *       - SessionDraft: renamed riskConfirmed → setupConfirmed (broadened
 *         to step-level confirmation, decoupled from riskSnapshot lifecycle)
 *       - TradingSetupSnapshot: same rename (on Session.tradingSetupSnapshot)
 * - v43: Risk profile selection (Mar 2026) [P05 S01 WU7C T09]
 *       - SessionDraft: added selectedRiskProfileId (selected risk profile for session)
 *       - TradingSetupRiskSnapshot: added selectedRiskProfileId and selectedRiskProfileName
 *         (audit-context fields frozen at confirmation time)
 * - v44: Risk profile provenance & divergence (Mar 2026) [P05 S01 WU7C T09]
 *       - SessionDraft: added selectedRiskProfileSource ('default' | 'manual' provenance)
 *       - SessionDraft: added riskProfileDefaultAckHash (divergence ack for default profile changes)
 * - v45: Session risk overrides & deviation notes (Mar 2026) [P05 S01 WU7C T10]
 *       - TradingSetupRiskSnapshot: replaced singular sessionOverride with
 *         sessionStrategyOverrides and sessionPlaybookOverrides arrays
 *       - SessionDraft: added sessionStrategyOverrides, sessionPlaybookOverrides, deviationNoteId
 * - v46: Risk derivation metadata (Mar 2026) [P05 S02 WU2F T06]
 *       - TradingSetupRiskSnapshot.pairs: added optional derivedFromLevel
 *         (RiskDerivationLevel) and derivedFromStage (RiskDerivationStage)
 *         per pair, persisted at confirmation time for truthful display
 * - v47: Per-override deviation notes (Mar 2026) [P05 S02 WU2F T06]
 *       - SessionStrategyOverride: added optional deviationNoteId
 *       - SessionPlaybookOverride: added optional deviationNoteId
 *       - Session-level deviationNoteId deprecated (legacy, never written)
 *
 * @see SYNC_TABLES for the authoritative list of synced tables
 */
export const SYNC_SCHEMA_VERSION = 47;

/**
 * Tombstone retention period in days.
 *
 * Tombstones older than this are excluded from baseline exports during
 * compaction, force baseline, or rebaseline operations. Local IndexedDB
 * retains all tombstones indefinitely for audit purposes.
 *
 * **Value**: 90 days
 * - Aligns with `MAX_DAYS_BETWEEN_BASELINES` in SyncEngine.ts
 * - Supports extended offline scenarios (vacations up to 12 weeks)
 * - Conservative default; can be adjusted based on usage patterns
 *
 * @see CODING_STANDARDS.md "Tombstone Retention Policy" for full documentation
 */
export const TOMBSTONE_RETENTION_DAYS = 90;

/**
 * Op log size warning threshold.
 *
 * When the number of pending (unsynced) operations exceeds this threshold,
 * a diagnostic warning is logged during export. This helps identify scenarios
 * where sync hasn't run for an extended period or rapid successive edits
 * are accumulating operations faster than expected.
 *
 * **Behavior**: Warning only - does not block export or sync operations.
 *
 * **Value**: 1000 pending operations
 * - Generous threshold for normal usage patterns
 * - Accounts for users with multiple devices syncing infrequently
 * - Can be adjusted based on observed usage patterns
 */
export const OP_LOG_WARNING_THRESHOLD = 1000;
